import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import WhatsAppButton from '../../../components/feature/WhatsAppButton';
import { blogPosts } from '../../../mocks/blogPosts';
import { articleContents } from '../../../mocks/articleContents';

const BlogArticlePage = () => {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => blogPosts.find((p) => p.slug === slug),
    [slug]
  );

  const content = useMemo(
    () => (slug ? articleContents[slug] : null),
    [slug]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return blogPosts
      .filter((p) => p.slug !== slug && p.category === article.category)
      .slice(0, 3);
  }, [article, slug]);

  const currentIndex = useMemo(
    () => blogPosts.findIndex((p) => p.slug === slug),
    [slug]
  );

  const prevArticle = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextArticle =
    currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | Blog Engeporto`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', article.excerpt);
      }
      const metaKw = document.querySelector('meta[name="keywords"]');
      if (metaKw) {
        metaKw.setAttribute('content', article.tags.join(', '));
      }

      // Canonical
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/blog/${article.slug}`);
      return () => { canonical?.remove(); };
    }
  }, [article]);

  if (!article || !content) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <i className="ri-file-search-line text-6xl text-neutral-300 mb-4" />
          <h1 className="text-3xl font-bold text-neutral-800 mb-4">
            Artigo não encontrado
          </h1>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:underline cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            Voltar ao Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const schemaArticle = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.dateISO,
    dateModified: article.dateISO,
    author: {
      '@type': 'Organization',
      name: 'Engeporto Engenharia e Serviços Ltda',
      url: import.meta.env.VITE_SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Engeporto Engenharia e Serviços Ltda',
      logo: {
        '@type': 'ImageObject',
        url: `${import.meta.env.VITE_SITE_URL}/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${import.meta.env.VITE_SITE_URL}/blog/${article.slug}`,
    },
    keywords: article.tags.join(', '),
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: import.meta.env.VITE_SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${import.meta.env.VITE_SITE_URL}/blog` },
        { '@type': 'ListItem', position: 3, name: article.title, item: `${import.meta.env.VITE_SITE_URL}/blog/${article.slug}` },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: schemaArticle }}
      />

      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-neutral-50 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3 md:py-4">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-neutral-600">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors cursor-pointer">
              Início
            </Link>
            <i className="ri-arrow-right-s-line text-neutral-400" />
            <Link to="/blog" className="hover:text-[#D4AF37] transition-colors cursor-pointer">
              Blog
            </Link>
            <i className="ri-arrow-right-s-line text-neutral-400" />
            <span className="text-neutral-800 font-medium line-clamp-1">
              {article.title}
            </span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
          {/* Conteúdo Principal */}
          <article className="lg:col-span-2">
            {/* Cabeçalho do Artigo */}
            <header className="mb-6 md:mb-8">
              <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                <span
                  className={`${article.categoryColor} text-white text-xs font-semibold px-2.5 py-1 md:px-3 rounded-full`}
                >
                  {article.category}
                </span>
                <span className="text-xs md:text-sm text-neutral-500">
                  {article.readTime} de leitura
                </span>
              </div>

              <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-3 md:mb-4 leading-tight font-['Poppins']">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 md:gap-4 text-xs md:text-sm text-neutral-600 mb-4 md:mb-6">
                <div className="flex items-center gap-1.5 md:gap-2">
                  <i className="ri-calendar-line text-[#D4AF37]" />
                  <time dateTime={article.dateISO}>{article.date}</time>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 md:gap-2 mb-4 md:mb-6">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs bg-neutral-100 text-neutral-700 px-2.5 py-1 md:px-3 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <img
                src={article.image}
                alt={article.title}
                className="w-full h-[220px] md:h-[400px] object-cover rounded-xl shadow-lg mb-6 md:mb-8"
              />
            </header>

            {/* Conteúdo do Artigo */}
            <div className="prose prose-lg max-w-none">
              {content.sections.map((section, idx) => (
                <section key={idx} className="mb-8 md:mb-10">
                  <h2 className="text-xl md:text-2xl font-bold text-neutral-900 mb-3 md:mb-4 flex items-center gap-2 md:gap-3">
                    <span className="w-1 h-6 md:h-8 bg-[#D4AF37] rounded-full" />
                    {section.title}
                  </h2>
                  {section.paragraphs.map((para, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-neutral-700 leading-relaxed mb-3 md:mb-4 text-sm md:text-base"
                      dangerouslySetInnerHTML={{ __html: para }}
                    />
                  ))}
                  {section.list && (
                    <ul className="space-y-2 md:space-y-3 my-4 md:my-6">
                      {section.list.map((item, lIdx) => (
                        <li
                          key={lIdx}
                          className="flex items-start gap-2 md:gap-3 text-neutral-700 text-sm md:text-base"
                        >
                          <i className="ri-checkbox-circle-fill text-[#D4AF37] text-lg md:text-xl mt-0.5 flex-shrink-0" />
                          <span dangerouslySetInnerHTML={{ __html: item }} />
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}

              {/* Conclusão */}
              {content.conclusion && (
                <section className="bg-neutral-50 border-l-4 border-[#D4AF37] p-4 md:p-6 rounded-lg my-8 md:my-10">
                  <h3 className="text-lg md:text-xl font-bold text-neutral-900 mb-2 md:mb-3 flex items-center gap-2">
                    <i className="ri-lightbulb-line text-[#D4AF37]" />
                    Conclusão
                  </h3>
                  <p
                    className="text-neutral-700 leading-relaxed text-sm md:text-base"
                    dangerouslySetInnerHTML={{ __html: content.conclusion }}
                  />
                </section>
              )}
            </div>

            {/* CTA no Artigo */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-[#0A1628] to-[#1a2942] text-white p-5 md:p-8 rounded-xl shadow-xl my-8 md:my-12"
            >
              <div className="flex flex-col md:flex-row items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-customer-service-2-line text-xl md:text-2xl text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-bold mb-2">
                    Precisa de Ajuda com {article.category}?
                  </h3>
                  <p className="text-neutral-300 mb-4 text-sm md:text-base">
                    Nossa equipe de especialistas está pronta para atender sua empresa em <strong>Sorocaba</strong> e <strong>Campinas</strong>.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href={`https://wa.me/5515996719308?text=${encodeURIComponent('Olá! Li um artigo no blog sobre ' + (article?.category ?? 'engenharia') + ' e gostaria de solicitar um orçamento.')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A1628] px-5 py-3 md:px-6 rounded-lg font-semibold hover:bg-[#C4A137] transition-all whitespace-nowrap cursor-pointer text-sm md:text-base"
                    >
                      <i className="ri-whatsapp-line" />
                      Solicitar Orçamento
                    </a>
                    <a
                      href="tel:+5515996719308"
                      className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-5 py-3 md:px-6 rounded-lg font-semibold hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer text-sm md:text-base"
                    >
                      <i className="ri-phone-line" />
                      (15) 99671-9308
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Navegação entre Artigos */}
            <nav className="border-t border-neutral-200 pt-6 md:pt-8 mt-8 md:mt-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
                {prevArticle && (
                  <Link
                    to={`/blog/${prevArticle.slug}`}
                    className="group flex items-start gap-3 md:gap-4 p-3 md:p-4 border border-neutral-200 rounded-lg hover:border-[#D4AF37] hover:shadow-md transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-left-line text-xl md:text-2xl text-[#D4AF37] mt-1" />
                    <div>
                      <p className="text-xs text-neutral-500 mb-1">
                        Artigo Anterior
                      </p>
                      <h4 className="text-xs md:text-sm font-semibold text-neutral-800 group-hover:text-[#D4AF37] transition-colors line-clamp-2">
                        {prevArticle.title}
                      </h4>
                    </div>
                  </Link>
                )}
                {nextArticle && (
                  <Link
                    to={`/blog/${nextArticle.slug}`}
                    className="group flex items-start gap-3 md:gap-4 p-3 md:p-4 border border-neutral-200 rounded-lg hover:border-[#D4AF37] hover:shadow-md transition-all cursor-pointer md:ml-auto text-right"
                  >
                    <div>
                      <p className="text-xs text-neutral-500 mb-1">
                        Próximo Artigo
                      </p>
                      <h4 className="text-xs md:text-sm font-semibold text-neutral-800 group-hover:text-[#D4AF37] transition-colors line-clamp-2">
                        {nextArticle.title}
                      </h4>
                    </div>
                    <i className="ri-arrow-right-line text-xl md:text-2xl text-[#D4AF37] mt-1" />
                  </Link>
                )}
              </div>
            </nav>
          </article>

          {/* Sidebar */}
          <aside className="lg:col-span-1 hidden lg:block">
            <div className="sticky top-24 space-y-8">
              {/* CTA Sidebar */}
              <div className="bg-[#0A1628] text-white p-6 rounded-xl shadow-lg">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-file-list-3-line text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">
                  Solicite um Orçamento
                </h3>
                <p className="text-sm text-neutral-300 mb-4">
                  Atendemos Sorocaba, Campinas e região com excelência técnica.
                </p>
                <a
                  href={`https://wa.me/5515996719308?text=${encodeURIComponent('Olá! Li um artigo no blog e gostaria de falar com um especialista.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center bg-[#D4AF37] text-[#0A1628] px-4 py-3 rounded-lg font-semibold hover:bg-[#C4A137] transition-all whitespace-nowrap cursor-pointer"
                >
                  Falar com Especialista
                </a>
              </div>

              {/* Artigos Relacionados */}
              {relatedArticles.length > 0 && (
                <div className="bg-neutral-50 p-6 rounded-xl border border-neutral-200">
                  <h3 className="text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2">
                    <i className="ri-article-line text-[#D4AF37]" />
                    Artigos Relacionados
                  </h3>
                  <div className="space-y-4">
                    {relatedArticles.map((related) => (
                      <Link
                        key={related.id}
                        to={`/blog/${related.slug}`}
                        className="group block cursor-pointer"
                      >
                        <div className="flex gap-3">
                          <img
                            src={related.image}
                            alt={related.title}
                            className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                          />
                          <div>
                            <h4 className="text-sm font-semibold text-neutral-800 group-hover:text-[#D4AF37] transition-colors line-clamp-2 mb-1">
                              {related.title}
                            </h4>
                            <p className="text-xs text-neutral-500">
                              {related.readTime}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <Link
                    to="/blog"
                    className="block mt-4 text-sm text-[#D4AF37] hover:underline cursor-pointer"
                  >
                    Ver todos os artigos →
                  </Link>
                </div>
              )}

              {/* Contato Rápido */}
              <div className="bg-white p-6 rounded-xl border border-neutral-200 shadow-sm">
                <h3 className="text-lg font-bold text-neutral-900 mb-4 flex items-center gap-2">
                  <i className="ri-phone-line text-[#D4AF37]" />
                  Contato Rápido
                </h3>
                <div className="space-y-3 text-sm">
                  <a
                    href="tel:+5515996719308"
                    className="flex items-center gap-3 text-neutral-700 hover:text-[#D4AF37] transition-colors cursor-pointer"
                  >
                    <i className="ri-smartphone-line text-lg" />
                    (15) 99671-9308
                  </a>
                  <a
                    href="mailto:contato@engeporto.com.br"
                    className="flex items-center gap-3 text-neutral-700 hover:text-[#D4AF37] transition-colors cursor-pointer"
                  >
                    <i className="ri-mail-line text-lg" />
                    contato@engeporto.com.br
                  </a>
                  <div className="flex items-start gap-3 text-neutral-700">
                    <i className="ri-map-pin-line text-lg mt-0.5" />
                    <span>Sorocaba e Campinas - SP</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default BlogArticlePage;